/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

/**
 * This illustrates how to create a widget that makes use of a number of different field definition types.
 */
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { ColorPickerFieldDefinition } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon/Icon";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xHeaderActions, xHeaderState } from "@monsma/App/Store/Reducers";
import { screen } from "@monsma/XDL/Helpers";
import Close from "@monsma/XDL/Icons/Times";
import parse from "html-react-parser";
import * as React from "react";
import { connect, DispatchProp } from "react-redux";
import { css } from "styled-components";

export interface NotificationBarStyles {
    container: InjectableCss;
    close: ButtonPresentationProps;
}

export const notificationBarStyles: NotificationBarStyles = {
    container: {
        css: css`
            height: 40px;
            text-align: center;
            font-size: 15px;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            ${({ backgroundColor }: any) => backgroundColor && `background: ${backgroundColor};`}
            ${({ notificationBarState }: any) => !notificationBarState && `display: none;`}
          
            p {
                margin: 0 1px;
                line-height: 1;
            }


            ${screen("lt", "md")} {
              padding: 0 30px 0 10px;
              
                * {
                  font-size: 12px; 
                  text-align: left;
                }
            }
        `,
    },
    close: {
        css: css`
            position: absolute;
            right: 10px;
            top: 10px;
            color: white;
        `,
    },
};

// this is used to help ensure that the names match between props.fields and definition.fieldDefinitions[].name
const enum fields {
    notificationBar = "notificationBar",
    backgroundColor = "backgroundColor",
}

const mapStateToProps = (state: xHeaderState) => ({
    isSticky: state.xHeader.isSticky,
    notificationBarState: state.xHeader.notificationBar,
});

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    DispatchProp<xHeaderActions> & {
        fields: {
            [fields.notificationBar]: string;
            [fields.backgroundColor]: ColorPickerFieldDefinition;
        };
    };

const NotificationBar: React.FC<Props> = ({
    fields: { backgroundColor, notificationBar },
    dispatch,
    isSticky,
    notificationBarState,
}) => {
    const onClose = () => {
        dispatch({ type: "xHeader/notificationBar", notificationBar: false });
    };
    return (
        <StyledWrapper
            {...notificationBarStyles.container}
            {...({ backgroundColor, isSticky, notificationBarState } as any)}
        >
            <Clickable
                {...notificationBarStyles.close}
                onClick={onClose}
                data-test-selector="notificationBar_sitemessage_close"
            >
                <Icon size={18} src={Close} color="white" />
            </Clickable>
            {notificationBar && parse(notificationBar, parserOptions)}
        </StyledWrapper>
    );
};

const definition: WidgetDefinition = {
    group: "Header",
    displayName: "Notification Bar",
    allowedContexts: ["Header"],
    fieldDefinitions: [
        {
            name: fields.notificationBar,
            displayName: "Notification Bar Content",
            editorTemplate: "RichTextField",
            defaultValue: "",
            fieldType: "General",
            placeholder: "your content goes here",
            isRequired: true,
        },
        {
            name: fields.backgroundColor,
            displayName: "Background Color",
            editorTemplate: "ColorPickerField",
            defaultValue: "",
            fieldType: "General",
            isRequired: true,
        },
    ],
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(NotificationBar),
    definition,
};

export default widgetModule;
